import React, { useState } from 'react';

import { c } from 'ttag';

import { Badge, Button } from '@freelancelabs/components';

import { OrganizationKind } from '../../graphql/__generated__/schema';
import MfaRequirementDialog from './MfaRequirementDialog';

interface Props {
    accountId: string;
    accountName: string;
    /** Null when the query has not answered yet, or answered without the field. */
    mfaRequired: boolean | null | undefined;
    /** Freelance.com is `internal`; every client account is not. */
    kind: OrganizationKind | null | undefined;
}

/**
 * Whether an account requires multi-factor authentication of its users, and the
 * gesture that changes it (PCG-303).
 *
 * **On the account and not on the person**, decided: the real demand from a client is
 * « MFA for all my people », and per user would make an operator remember it at every
 * invitation. There is no per-person derogation, which is exactly the case-by-case
 * administration this granularity rules out.
 *
 * **It sits with the other three panels** because the four read as one sequence of
 * decisions about a client: may the account receive portal users, what may it see,
 * what may it be shown inside a module, and how hard its people must authenticate.
 *
 * **The panel says what the flag IS NOT, in words, and that is most of its value.**
 * « MFA requis » alone gets read as « ces gens ont le MFA », which is a different fact
 * living in the Cognito pool. This one is a policy: it says what is demanded, never
 * what anybody actually registered. The screen cannot show the second, so it must not
 * let the first be mistaken for it.
 *
 * The button is **never disabled on a rule the server owns**, same as its neighbour:
 * the front cannot know the actor's permissions (`viewer` exposes the role, not the
 * rights behind it), so the control stays visible and the refusal is rendered after
 * the click, naming the right that is missing.
 */
const MfaRequirementPanel = ({ accountId, accountName, mfaRequired, kind }: Props) => {
    const [pending, setPending] = useState<boolean | null>(null);

    /**
     * Freelance.com carries no such policy: the mutation refuses it with
     * `INTERNAL_ORGANIZATION_NOT_ALLOWED`, and offering a button whose only outcome is
     * a refusal is offering a dead control. Same guard, same reason, as the three
     * panels beside it.
     *
     * Multi-factor authentication for colleagues is not absent from the world, it is
     * decided on the pool rather than account by account; the refusal sentence says so
     * rather than pretending the subject does not exist.
     */
    if (kind === OrganizationKind.Internal) {
        return null;
    }

    /**
     * Null is not « non requis ». Rendering the panel on a missing field would offer
     * « Exiger le MFA » on an account that may already require it, and the operator
     * would read the absence as an answer.
     */
    if (mfaRequired === null || mfaRequired === undefined) {
        return null;
    }

    return (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-border-norm bg-background-norm px-4 py-3">
            <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-sm font-semibold text-text-900">
                        {c('MFA requirement').t`Multi-factor authentication`}
                    </h2>
                    <Badge category={mfaRequired ? 'success' : 'default'} size="small">
                        {mfaRequired ? c('MFA requirement').t`Required` : c('MFA requirement').t`Not required`}
                    </Badge>
                </div>
            </div>

            <Button className="ml-auto" category="weak" onClick={() => setPending(!mfaRequired)}>
                {mfaRequired
                    ? c('MFA requirement action').t`Stop requiring it…`
                    : c('MFA requirement action').t`Require it…`}
            </Button>

            {pending !== null && (
                <MfaRequirementDialog
                    accountId={accountId}
                    accountName={accountName}
                    required={pending}
                    onClose={() => setPending(null)}
                />
            )}
        </div>
    );
};

export default MfaRequirementPanel;
